import { createApi } from "@reduxjs/toolkit/dist/query/react"
import {
  ADDON_AUTOCOMPLETE_ENDPOINT,
  BILLING_CONFIGS_ENDPOINT,
  BILLING_PLANS_ENDPOINT,
  ESTIMATE_BILLING_ENDPOINT,
  ORG_MONTHLY_USAGE_ENDPOINT,
  RTK_SLICE_API_LIST
} from "../../common/constants"
import { baseQuery, getTags } from "../api"
import { getQueryString } from "../../common/utils"
import {
  BillingConfig,
  BillingConfigDetails,
  BillingPlansResponse,
  EstimateBillingEnvelope,
  EstimateBillingRequest,
  EstimateBillingResponse,
  FetchOrgMonthlyUsageArgs,
  OrgMonthlyUsageData,
  OrgMonthlyUsageResponse
} from "../../types"
import bbApi from "../../api"

const reducerPath = "billingConfigApi"
const billingConfigPath = "billing-config"

export const billingConfigApi = createApi({
  reducerPath,
  baseQuery,
  tagTypes: [billingConfigPath],
  endpoints: builder => ({
    fetchBillingConfig: builder.query({
      query: (params: { org_id: string }) =>
        `${BILLING_CONFIGS_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: BillingConfig[] | BillingConfig) => {
        if (Array.isArray(responseData)) return responseData
        if (responseData && typeof responseData === "object")
          return [responseData]
        return []
      },
      providesTags: result => getTags(billingConfigPath, "id", result)
    }),
    updateOrCreateBillingConfig: builder.mutation({
      queryFn: async (
        payload: Partial<BillingConfig> & {
          org_id: string
          details: BillingConfigDetails
        }
      ) => {
        if (!payload.id) {
          delete payload.id
        }
        const data = await bbApi.billingConfig.createOrUpdate(payload)
        return { data }
      },
      invalidatesTags: (result, error, arg) => {
        const resultData = result as
          | { status?: string; message?: string }
          | undefined
        const isErrorStatus = resultData?.status === "error"
        if (error || isErrorStatus) {
          return []
        }
        return [
          { type: billingConfigPath, id: arg.id },
          { type: billingConfigPath, id: RTK_SLICE_API_LIST }
        ]
      }
    }),
    fetchBillingPlans: builder.query<BillingPlansResponse, void>({
      query: () => BILLING_PLANS_ENDPOINT
    }),
    deleteBillingConfig: builder.mutation({
      queryFn: async (id: string) => {
        const data = await bbApi.billingConfig.remove(id)
        return { data }
      },
      invalidatesTags: () => [
        { type: billingConfigPath, id: RTK_SLICE_API_LIST }
      ]
    }),
    fetchAddonAutocomplete: builder.query<
      {
        type: string
        monthly_price: number
        label: string
        description: string
      }[],
      { search: string }
    >({
      query: (params: { search: string }) =>
        `${ADDON_AUTOCOMPLETE_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: {
        status: string
        addons?: {
          type: string
          monthly_price: number
          label: string
          description: string
        }[]
      }) => responseData?.addons ?? []
    }),
    estimateBilling: builder.query<
      EstimateBillingResponse,
      EstimateBillingRequest
    >({
      query: body => ({
        url: ESTIMATE_BILLING_ENDPOINT,
        method: "POST",
        body
      }),
      transformResponse: (env: EstimateBillingEnvelope) => {
        if (env?.status !== "success") {
          throw new Error(env?.message || "Estimate failed")
        }
        // The endpoint returns { status, total, breakdown } at the top
        // level (not nested under `data` as the original spec suggested).
        // Fall back to env.data for forward-compatibility if the shape
        // ever changes.
        if (env.data) return env.data
        const { total, breakdown } = env
        if (total === undefined || !breakdown) {
          throw new Error("Malformed estimate response")
        }
        return { total, breakdown }
      }
    }),
    fetchOrgMonthlyUsage: builder.query<
      OrgMonthlyUsageData,
      FetchOrgMonthlyUsageArgs
    >({
      query: params =>
        `${ORG_MONTHLY_USAGE_ENDPOINT}${getQueryString({ ...params })}`,
      transformResponse: (env: OrgMonthlyUsageResponse) => {
        if (env?.status !== "success") {
          throw new Error(env?.message || "Failed to fetch org usage")
        }
        // The endpoint returns { status, org_id, month, year, period,
        // usage } at the top level (not nested under `data`). Fall
        // back to env.data for forward-compat.
        if (env.data) return env.data
        const { org_id, month, year, period, usage } = env
        if (!usage) {
          throw new Error("Malformed org usage response")
        }
        return {
          org_id: org_id ?? "",
          month: month ?? "",
          year: year ?? 0,
          period: period ?? { start: "", stop: "" },
          usage
        }
      }
    })
  })
})

export const {
  useFetchBillingConfigQuery,
  useFetchBillingPlansQuery,
  useUpdateOrCreateBillingConfigMutation,
  useDeleteBillingConfigMutation,
  useFetchAddonAutocompleteQuery,
  useEstimateBillingQuery,
  useFetchOrgMonthlyUsageQuery
} = billingConfigApi
